import { Alert, Box, Button, Card, CardContent, IconButton, Link, Stack, Tooltip, Typography } from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { reportError } from "../utils/errorReporting";
import RefreshIcon from "@mui/icons-material/Refresh";
import HomeIcon from "@mui/icons-material/Home";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const logo = "/airplx-logo-transparent-square-no-text.png";

interface Props extends WithTranslation {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackDescription?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

class ErrorBoundaryComponent extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
      copied: false,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Report error to backend (logs + email notification)
    // This works in both dev and production
    reportError(error, errorInfo.componentStack ?? undefined);

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error info
    this.setState({
      errorInfo,
    });
  }

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/#/hangars";
  };

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const errorText = [
      `Error: ${error?.message || "Unknown error"}`,
      "",
      "Stack Trace:",
      error?.stack || "No stack trace",
      "",
      "Component Stack:",
      errorInfo?.componentStack || "No component stack",
    ].join("\n");

    navigator.clipboard.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch((err) => {
      console.warn("Failed to copy error details:", err);
    });
  };

  render() {
    if (this.state.hasError) {
      const { t } = this.props;

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            bgcolor: "background.default",
            p: 3,
          }}
        >
          <Card
            sx={{
              maxWidth: 480,
              width: "100%",
              textAlign: "center",
              boxShadow: 3,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* AirPlx Logo */}
              <Box
                component="img"
                src={logo}
                alt="AirPlx Logo"
                sx={{
                  width: 80,
                  height: 80,
                  mb: 3,
                  opacity: 0.9,
                }}
              />

              {/* Title */}
              <Typography
                variant="h5"
                gutterBottom
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {t('dialogs.error.defaultTitle', 'Oops! Something went wrong')}
              </Typography>

              {/* Description */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {t('dialogs.error.defaultDescription', 'An unexpected error occurred.')}
              </Typography>

              {/* Team notified message */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 4 }}
              >
                {t('dialogs.error.teamNotified', 'The AirPlx team has been notified and is looking into it.')}
              </Typography>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleRefresh}
                  size="large"
                >
                  {t('dialogs.error.refreshButton', 'Refresh Page')}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  size="large"
                >
                  {t('dialogs.error.returnHome', 'Return Home')}
                </Button>
              </Stack>

              {/* Dev mode: Error details */}
              {process.env.NODE_ENV === "development" && this.state.error && (
                <Alert
                  severity="warning"
                  sx={{ mt: 3, textAlign: "left" }}
                  action={
                    <Tooltip title={this.state.copied ? "Copied!" : "Copy error details"}>
                      <IconButton
                        size="small"
                        onClick={this.handleCopyError}
                        sx={{ color: this.state.copied ? "success.main" : "inherit" }}
                      >
                        {this.state.copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  }
                >
                  <Typography variant="subtitle2" gutterBottom>
                    {t('dialogs.error.errorDetailsLabel', 'Error Details (dev only):')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-word" }}
                  >
                    {this.state.error.message}
                  </Typography>
                  {this.state.errorInfo && (
                    <details style={{ marginTop: "8px" }}>
                      <summary style={{ cursor: "pointer", fontSize: "0.75rem" }}>
                        {t('dialogs.error.componentStackLabel', 'Component Stack')}
                      </summary>
                      <pre style={{ fontSize: "0.65rem", overflow: "auto", maxHeight: 200 }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Footer link */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
            Need help?{" "}
            <Link href="mailto:support@airplx.com" underline="hover">
              Contact Support
            </Link>
          </Typography>
        </Box>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryComponent);
